import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Brain } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { pct, quizSubtitle } from "@/lib/quiz";

export function QuizHistory() {
  const [all, setAll] = useState(false);
  const { data = [], isLoading } = useQuery({ queryKey: ["quiz-history"], queryFn: () => api.get("/quizzes/history?limit=100").then((r) => r.data) });
  const correct = data.reduce((s, a) => s + a.score, 0);
  const total = data.reduce((s, a) => s + a.total, 0);
  const shown = all ? data : data.slice(0, 5);

  return (
    <section data-testid="quiz-history">
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow">Quiz history</div>
        {total > 0 && <div className="text-xs text-[#A1A1AA]" data-testid="quiz-history-accuracy">{pct(correct, total)}% accuracy · {data.length} quiz{data.length === 1 ? "" : "zes"}</div>}
      </div>
      {isLoading && <div className="card h-24 animate-pulse" />}
      {!isLoading && data.length === 0 && (
        <Link to="/quizzes" className="card p-4 flex items-center gap-3 hover:border-[#3F3F46] transition-colors" data-testid="quiz-history-empty">
          <Brain size={20} className="text-[#E50914]" />
          <span className="text-sm text-[#A1A1AA] flex-1">No quizzes yet. Test your BJJ knowledge.</span>
          <ChevronRight size={16} className="text-[#52525B]" />
        </Link>
      )}
      {shown.length > 0 && (
        <div className="card divide-y divide-[#1F1F23] overflow-hidden">
          {shown.map((a) => {
            const p = pct(a.score, a.total);
            return (
              <Link key={a.id} to={`/quiz/${a.id}`} data-testid={`quiz-history-item-${a.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-[#1A1A1D] transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${a.kind === "personal" ? "bg-[#E50914]/15 text-[#FF6B6B]" : "bg-[#222225] text-[#A1A1AA]"}`}>{a.kind === "personal" ? "Personal" : "General"}</span>
                    <span className="text-sm font-semibold truncate">{a.title}</span>
                  </div>
                  <div className="text-xs text-[#71717A] mt-1 truncate">{formatDate(a.completed_at.slice(0, 10))}{quizSubtitle(a) ? ` · ${quizSubtitle(a)}` : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold tabular-nums">{a.score}/{a.total}</div>
                  <div className={`text-xs tabular-nums ${p >= 70 ? "text-[#10B981]" : p >= 50 ? "text-[#F59E0B]" : "text-[#EF4444]"}`}>{p}%</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
      {data.length > 5 && (
        <button data-testid="quiz-history-toggle" onClick={() => setAll(!all)} className="w-full h-10 text-xs font-semibold text-[#A1A1AA] hover:text-white transition-colors mt-1">
          {all ? "Show less" : `Show all ${data.length}`}
        </button>
      )}
    </section>
  );
}
